import { ControlBase } from "aic";
import { omit } from "lodash";
import { useRef, useState } from "react";
import { Button, Collapse, Form } from "react-bootstrap";
import { ChevronDown, ChevronRight, Pen } from "react-bootstrap-icons";
import { getFromDataPath } from "../../utility";
import { classMerge } from "../../utility/classMerge";
import { useARSContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { isWhereClause } from "../typeGuards";
import { formatWhereClauseAsCode } from "../utility/formatWhereClauseAsCode";
import { parseWhereClauseFromCode } from "../utility/parseWhereClauseFromCode";
import styles from "./WhereClauseConditionItemControl.module.css";
import { WhereClauseEditor } from "./WhereClauseEditor";

/**
 * Props for the WhereClauseManager component.
 */
export interface WhereClauseManagerProps {
  /** RFC 6901 JSON Pointer path to the entity (AnalysisSet, DataSubset, or Group) in ARSContext state */
  dataPath: string;
}

/**
 * Manages where clause conditions for entities (AnalysisSet, DataSubset, or Group).
 *
 * This component displays a compact code representation by default (e.g., "ADSL.SAFFL = 'Y'")
 * and expands to show detailed controls when toggled. The compact view provides inline editing
 * with real-time validation, while the expanded view shows the full where clause editor.
 *
 * The component starts in compact mode showing a formatted code representation that can be
 * clicked to expand to the full editor. The compact/expanded state is managed internally.
 *
 * Inline editing mode allows users to directly edit the code representation with real-time validation.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the entity in ARSContext state.
 * @returns A React functional component rendering the where clause manager.
 */
export const WhereClauseManager = ({ dataPath }: WhereClauseManagerProps): React.ReactElement => {
  const { state, dispatch } = useARSContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const cancelRef = useRef(false);

  // Get the current entity from state
  const whereClause = getFromDataPath(dataPath, state, isWhereClause);

  // Format the where clause as code
  const compactCode = whereClause ? formatWhereClauseAsCode(whereClause, state.variables) : "";

  // Get available variables
  const availableVariables = state.variables.map((v) => v.value);

  // Validate edit value
  const parsedWhereClause =
    isEditing && editValue ? parseWhereClauseFromCode(editValue, availableVariables) : undefined;
  const isValid = parsedWhereClause !== false;
  const isInvalid = isEditing && editValue.length > 0 && !isValid;

  /**
   * Handles entering edit mode.
   */
  const handleStartEdit = (): void => {
    setEditValue(compactCode);
    setIsEditing(true);
    cancelRef.current = false;
  };

  /**
   * Saves the changes to the where clause.
   *
   * @param value - The value to save
   */
  const saveChanges = (value: string): void => {
    const parsed = parseWhereClauseFromCode(value, availableVariables);

    if (parsed) {
      const toOmit = "condition" in parsed ? "compoundExpression" : "condition";
      const newValue = { ...omit(whereClause, toOmit), ...parsed };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath,
        value: newValue,
      });
    }
    setIsEditing(false);
  };

  /**
   * Handles key down events in edit mode.
   *
   * @param e - Keyboard event.
   */
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    /* istanbul ignore else -- Handler only processes keys in edit mode */
    if (isEditing) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        cancelRef.current = true;
        /* istanbul ignore else -- Event target should always be an HTMLElement */
        if (e.target instanceof HTMLElement) {
          e.target.blur();
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        /* istanbul ignore else -- Event target should always be an HTMLElement */
        if (e.target instanceof HTMLElement) {
          e.target.blur();
        }
      }
    }
  };

  /**
   * Handles the blur event or escape press for the input field.
   * Saves changes if the blur was not caused by a cancel action.
   *
   * @param e - The focus event from the blur.
   */
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
    /* istanbul ignore else -- Blur should only occur when editing */
    if (cancelRef.current) {
      // Canceling - revert to original value
      setEditValue(compactCode);
      cancelRef.current = false;
      setIsEditing(false);
    } else if (isEditing) {
      // Get the actual input value from the event target to avoid stale state closures
      const inputElement = e.target as HTMLInputElement;
      saveChanges(inputElement.value);
    }
  };

  const idPath = `${dataPath.replace(/\//g, "-")}`;

  return !whereClause ? (
    <span className="text-danger">Entity not found at path: {dataPath}</span>
  ) : (
    <div>
      {/* Compact view */}
      <div
        className={styles.compactContainer}
        onKeyDownCapture={handleKeyDown}
        onBlur={handleBlur}
      >
        <Button
          id={`${idPath}-toggle-expand`}
          onClick={() => setIsExpanded(!isExpanded)}
          className={styles.toggleButton}
          variant="link"
          size="sm"
          aria-label={isExpanded ? "Collapse" : "Expand"}
          disabled={isEditing}
        >
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </Button>
        <div
          className="flex-grow-1"
          style={{ minWidth: 0 }}
        >
          {!isEditing ? (
            <div
              className="form-control form-control-sm"
              style={{ border: "none" }}
            >
              <code
                className={styles.codeBlock}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {compactCode || <span className="text-muted">No where clause defined</span>}
              </code>
            </div>
          ) : (
            <ControlBase
              id={`${idPath}-edit-input`}
              aria-label="Enter where clause"
              disabled={false}
              autoFocus={isEditing}
              value={editValue}
              whiteSpaceFix={true}
              setValue={setEditValue}
              spellCheck={false}
              className={classMerge([
                isEditing && isValid && editValue.length > 0
                  ? "is-valid"
                  : isInvalid
                    ? "is-invalid"
                    : "",
              ])}
            />
          )}
        </div>
        {!isEditing && (
          <div className={styles.actionButtons}>
            <Pen
              id={`${idPath}-edit`}
              className="text-primary"
              role="button"
              aria-label="Edit where clause"
              onClick={handleStartEdit}
            />
          </div>
        )}
      </div>
      {/* Validation feedback */}
      <Form.Text
        style={{
          height: isEditing ? "1.5em" : 0,
          minHeight: isEditing ? "1.5em" : 0,
          transition: "height 0.2s ease, min-height 0.2s ease",
        }}
        className={classMerge([
          isValid && editValue.length > 0
            ? "valid-feedback"
            : isInvalid
              ? "invalid-feedback"
              : "text-muted",
          styles.validationText,
        ])}
      >
        {isValid && editValue.length > 0
          ? "Valid where clause"
          : isInvalid
            ? "Invalid where clause format"
            : ""}
      </Form.Text>
      {/* Expanded view */}
      <Collapse in={isExpanded}>
        <div>
          <WhereClauseEditor dataPath={dataPath} />
        </div>
      </Collapse>
    </div>
  );
};

WhereClauseManager.displayName = "WhereClauseManager";
